import { pipe } from 'fp-ts/function';
import { option } from 'fp-ts';
import { Option } from 'fp-ts/Option';
import { BillType, Counter, EnterpriseScopeCounterDiscriminator } from '@mews-commander/data-access';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { CounterPageTab } from './types';

export const getBillCounterValues = (counter: Counter) => {
    switch (counter.data.discriminator) {
        case EnterpriseScopeCounterDiscriminator.AvailabilityBlockCounter:
        case EnterpriseScopeCounterDiscriminator.FiscalCounter:
        case EnterpriseScopeCounterDiscriminator.RegistrationCardCounter:
        case EnterpriseScopeCounterDiscriminator.ServiceOrderCounter:
            return {
                title: '',
                billTypeCode: '',
                displayCid: false,
                creditNoteTitle: '',
            };

        case EnterpriseScopeCounterDiscriminator.AccountingCounter:
        case EnterpriseScopeCounterDiscriminator.BillPreviewCounter:
        case EnterpriseScopeCounterDiscriminator.ProformaCounter:
            return {
                title: counter.data.value.title,
                billTypeCode: '',
                displayCid: false,
                creditNoteTitle: '',
            };

        case EnterpriseScopeCounterDiscriminator.BillCounter:
        case EnterpriseScopeCounterDiscriminator.CorrectionBillCounter:
        case EnterpriseScopeCounterDiscriminator.PaymentConfirmationBillCounter:
            return {
                title: counter.data.value.title,
                billTypeCode: counter.data.value.billTypeCode,
                displayCid: counter.data.value.displayCid,
                creditNoteTitle: counter.data.value.creditNoteTitle ?? '',
            };

        default:
            throw new Error('Not supported Counter Type');
    }
};

export const useIsNonRegionalCreditNoteCounterEnabled = () => {
    const nonRegional = useFeatureFlag(FeatureFlag.creditNoteBillCounter);
    const regional = useFeatureFlag(FeatureFlag.creditNoteCounterForItalyBasqueSpain);

    return nonRegional && !regional;
};

export const canCreateCounters = (tab: CounterPageTab) =>
    tab !== CounterPageTab.SystemGeneratedCounters && tab !== CounterPageTab.CreditNoteCounters;

export const findCounterByBillType = (billType: Option<BillType>) => (counter: Counter) =>
    pipe(
        option.some(counter),
        option.chain(({ data }) =>
            data?.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter
                ? option.some(data.value.applicableBillType)
                : option.none
        ),
        option.bind('cleanBillType', () => billType),
        option.chain(({ applyToBill, applyToInvoice, cleanBillType }) =>
            (applyToBill && cleanBillType === BillType.Receipt) ||
            (applyToInvoice && cleanBillType === BillType.Invoice) ||
            (applyToInvoice && applyToBill)
                ? option.some(true)
                : option.none
        ),
        option.isSome
    );
